// Casamento de leads pela chave do índice único `unique_lead_per_org_idx`:
//
//   CREATE UNIQUE INDEX unique_lead_per_org_idx ON public.leads
//     (org_id, name, COALESCE(phone,''), COALESCE(email::text,''))
//     WHERE (phone IS NOT NULL OR email IS NOT NULL);
//
// Serve para, depois de um 23505, descobrir QUAL lead já existia — e assim reaproveitá-lo
// em vez de devolver erro ao usuário.
//
// Duas particularidades do banco que o casamento tem que respeitar:
//  1. `COALESCE(...,'')` faz NULL e string vazia colidirem: são o mesmo valor na chave.
//  2. O trigger `normalize_phone_leads` reescreve o telefone no INSERT (adiciona DDI 55 e
//     insere o 9º dígito em celular), então o valor gravado difere do que o front enviou.
//     Por isso os dois lados passam por `normalizePhone` antes de comparar.

import { normalizePhone } from '@/lib/sonax/normalizePhone';

export type LeadContactKey = {
  phone?: string | null;
  email?: string | null;
};

/** Par (telefone, e-mail) normalizado na mesma semântica da chave do índice. */
export function leadContactKey(lead: LeadContactKey): { phone: string; email: string } {
  return {
    phone: normalizePhone(lead.phone ?? null) ?? '',
    email: (lead.email ?? '').trim().toLowerCase(),
  };
}

/**
 * Escolhe, entre candidatos que já compartilham `(org_id, name)`, o lead que colidiu.
 *
 * Prioridade: match exato de telefone **e** e-mail (a chave completa) e, se nenhum casar,
 * um fallback best-effort por telefone e depois por e-mail — cobre o caso de o outro campo
 * divergir por normalização que não replicamos aqui.
 */
export function matchLeadByContactKey<T extends LeadContactKey>(
  candidates: readonly T[],
  key: LeadContactKey,
): T | null {
  const want = leadContactKey(key);
  const normalized = candidates.map((lead) => ({ lead, ...leadContactKey(lead) }));

  const exact = normalized.find((c) => c.phone === want.phone && c.email === want.email);
  if (exact) return exact.lead;

  const byPhone = want.phone ? normalized.find((c) => c.phone === want.phone) : undefined;
  if (byPhone) return byPhone.lead;

  const byEmail = want.email ? normalized.find((c) => c.email === want.email) : undefined;
  return byEmail?.lead ?? null;
}
